import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateCompanyBrandColor, type CompanyBrandColor } from '../services/api/companies';
import type { Company } from '../services/api/auth';
import { accountManager } from '../utils/accountManager';

/**
 * Saves the company's theme colour, then writes it into the signed-in session's
 * cached company — the snapshot App.tsx paints from — so it applies at once and a
 * reload does not flash the old colour before revalidation catches up.
 */
export function useUpdateCompanyBrandColor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, colors }: { companyId: string; colors: CompanyBrandColor }) =>
      updateCompanyBrandColor(companyId, colors),
    onSuccess: (saved) => {
      const withBrand = (company: Company | null): Company | null => {
        if (!company) return company;
        const { primaryColor: _l, primaryColorDark: _d, ...rest } = company.branding ?? {};
        const branding = {
          ...rest,
          ...(saved.brandColor ? { primaryColor: saved.brandColor } : {}),
          ...(saved.brandColorDark ? { primaryColorDark: saved.brandColorDark } : {}),
        };
        return { ...company, branding: Object.keys(branding).length ? branding : undefined };
      };

      const session = accountManager.getActiveSession();
      if (session) {
        accountManager.updateActiveCompany(
          session.activeCompanyId,
          session.accessToken,
          session.refreshToken,
          session.expiresAt,
          withBrand(session.activeCompany)
        );
      }
      qc.setQueryData(['auth', 'state'], (prev: { company: Company | null } | undefined) =>
        prev ? { ...prev, company: withBrand(prev.company) } : prev
      );
    },
  });
}
